import React from 'react';
import styled from '@emotion/styled';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const DashboardContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(180deg, #000000 0%, #0A1128 100%);
  color: white;
  display: flex;
`;

const Sidebar = styled.div`
  width: 250px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  padding: 2rem 0;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  display: flex;
  flex-direction: column;
`;

const SidebarLogo = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(to right, #ffffff, #4B63D3);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: 0.1rem;
  padding: 0 2rem 2rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const SidebarNav = styled.nav`
  margin-top: 2rem;
  flex: 1;
`;

const NavItem = styled.button<{ active?: boolean }>`
  width: 100%;
  padding: 1rem 2rem;
  background: ${props => props.active ? 'rgba(75, 99, 211, 0.1)' : 'transparent'};
  border: none;
  color: ${props => props.active ? '#ffffff' : 'rgba(255, 255, 255, 0.7)'};
  text-align: left;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-left: 3px solid ${props => props.active ? '#4B63D3' : 'transparent'};

  &:hover {
    background: rgba(75, 99, 211, 0.1);
    color: white;
  }
`;

const LogoutButton = styled(NavItem)`
  color: #ff4444;
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  &:hover {
    background: rgba(255, 68, 68, 0.1);
    color: #ff4444;
  }
`;

const MainSection = styled.div`
  margin-left: 250px;
  width: calc(100% - 250px);
  min-height: 100vh;
`;

const MainContent = styled.main`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const WelcomeSection = styled.div`
  background: rgba(75, 99, 211, 0.1);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(75, 99, 211, 0.2);
  backdrop-filter: blur(10px);
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    border-color: rgba(75, 99, 211, 0.4);
  }
`;

const CardTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: #4B63D3;
`;

const CardContent = styled.p`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
`;

const UserInfo = styled.div`
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.05);
  margin: 0 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
`;

// Icons (you can replace these with actual icons from a library like react-icons)
const HomeIcon = () => <span>🏠</span>;
const SettingsIcon = () => <span>⚙️</span>;
const LogoutIcon = () => <span>🚪</span>;

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <DashboardContainer>
      <Sidebar>
        <SidebarLogo>COSMOS</SidebarLogo>
        <UserInfo>{user?.email}</UserInfo>
        <SidebarNav>
          <NavItem 
            active={location.pathname === '/dashboard'}
            onClick={() => handleNavigation('/dashboard')}
          >
            <HomeIcon /> Home
          </NavItem>
          <NavItem 
            active={location.pathname === '/dashboard/settings'}
            onClick={() => handleNavigation('/dashboard/settings')}
          >
            <SettingsIcon /> Settings
          </NavItem>
        </SidebarNav>
        <LogoutButton onClick={handleLogout}>
          <LogoutIcon /> Logout
        </LogoutButton>
      </Sidebar>

      <MainSection>
        <MainContent>
          <WelcomeSection>
            <h1>Welcome to Cosmos</h1>
            <p>Your gateway to the digital universe</p>
          </WelcomeSection>

          <GridContainer>
            <Card>
              <CardTitle>Quick Stats</CardTitle>
              <CardContent>
                View your activity metrics and performance indicators at a glance.
              </CardContent>
            </Card>

            <Card>
              <CardTitle>Recent Activity</CardTitle>
              <CardContent>
                Track your recent interactions and updates within the system.
              </CardContent>
            </Card>

            <Card>
              <CardTitle>Resources</CardTitle>
              <CardContent>
                Access documentation, guides, and helpful resources.
              </CardContent>
            </Card>
          </GridContainer>
        </MainContent>
      </MainSection>
    </DashboardContainer>
  );
};

export default Dashboard; 